import { $, html, rune, type Html, View } from 'rune-ts';
import klass from './LinksSection.module.scss';
import { staticTypoPcMo, typoPcMo } from '../../../../../shared/typography/typo';
import { ButtonArrow } from '../../../../components/atoms/ButtonArrow/ButtonArrow';
import { getResizedUrl } from '../../../../../shared/util/image';
import Swiper from 'swiper';
import { Autoplay, EffectFade, Navigation } from 'swiper/modules';
import { range, map, pipe, toArray } from '@fxts/core';
import { htmlIf } from '../../../../../shared/util';

interface CreatorCardProps {
  card_number: number;
}

class CreatorCard extends View<CreatorCardProps> {
  override template({ card_number }: CreatorCardProps): Html {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;
    const image_urls: Record<number, string> = {
      1: '//s3.marpple.co/files/u_4648126/2024/10/original/8db6d6a14574e0b9aeabd49b12f911b101be74d43.png',
      2: '//s3.marpple.co/files/u_4648126/2024/10/original/f55bf5bae6ceb1c2f6e8d8ef78dac919fd4f5c605.png',
      3: '//s3.marpple.co/files/u_4648126/2024/10/original/c763d78c10c3ab71882e5d730cf29700690c4eab4.png',
      4: '//s3.marpple.co/files/u_4648126/2024/11/original/add8d9a0b6dc565e2a090c968ee175ef4ae555572.png',
      5: '//s3.marpple.co/files/u_4648126/2024/11/original/0bf7de936b067e348fc732bd104f8b1ea4eff7b22.png',
    };
    const mo_image_urls: Record<number, string> = {
      1: '//s3.marpple.co/files/u_4648126/2024/10/original/a718486e9304f3c8cd7055773fabecba3198fde23.png',
      2: '//s3.marpple.co/files/u_4648126/2024/10/original/4516c56dd4da6415a8b44e4b5ac3f4a180f069175.png',
      3: '//s3.marpple.co/files/u_4648126/2024/10/original/67d289bace76723116450f51a5226b7f4101a12b4.png',
      4: '//s3.marpple.co/files/u_4648126/2024/11/original/62090d8ae768dfbbbcfab213397d3c15bf0ae9131.png',
      5: '//s3.marpple.co/files/u_4648126/2024/11/original/cd76ee22584d98c9de8ad04656fe00bde74b180b1.png',
    };
    const creator_links: Record<number, string> = {
      1: 'https://marpple.shop/kr/bbosoong_peach',
      2: 'https://marpple.shop/kr/kmins',
      3: 'https://marpple.shop/kr/jjoma',
      4: 'https://marpple.shop/kr/little_waegjeo',
      5: 'https://marpple.shop/kr/tukata',
    };
    return html`
      <a href="${creator_links[card_number] ?? ''}">
        <img
          src="${getResizedUrl({
            url: is_mobile ? (mo_image_urls[card_number] ?? '') : (image_urls[card_number] ?? ''),
            width: 480 * 2,
            quality: 85,
            format: 'webp',
          })}"
          alt="마플샵 핸드메이드 링크 이미지"
        />
      </a>
    `;
  }
}

export class LinkCreator extends View {
  swiper?: Swiper;
  current_index = 1;
  max_index = 5;

  constructor() {
    super({});
  }

  override template(): Html {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html`
      <div class="${klass.creator_wrapper}">
        <div class="${klass.description} ${typoPcMo({ is_mobile, pc: '20_medium', mo: '16_medium' })}">
          나만의 개성으로 꿈과 열정을 펼치고 있는
        </div>
        <div class="${staticTypoPcMo({ is_mobile, pc: 'bebas_48_bold', mo: 'bebas_32_bold' })}">
          MarppleShop <br />
          Handmade Creator
        </div>
        <div class="${klass.slide}">
          ${htmlIf(html`<div>${new ButtonArrow({ direction: 'left', type: 'dim' })}</div>`, !is_mobile)}
          <div class="${klass.swiper}">
            <div class="${klass.swiper_wrapper} swiper-wrapper">${this._renderCardList()}</div>
          </div>
          ${htmlIf(html`<div>${new ButtonArrow({ direction: 'right', type: 'dim' })}</div>`, !is_mobile)}
        </div>
        <div
          class="pagination ${klass.pagination} ${staticTypoPcMo({
            is_mobile,
            mo: 'bebas_14_bold',
            pc: 'bebas_16_bold',
          })}"
        >
          <span class="current_index"> 0${this.current_index} </span>
          <div class="${klass.pages}">
            <div class="progress ${klass.page}" style="width: ${this.calcProgress()}%;"></div>
          </div>
          <span> 0${this.max_index} </span>
        </div>
      </div>
    `;
  }

  override onRender() {
    this.swiper = this._onLoadSwiper();
  }

  private _onLoadSwiper() {
    return new Swiper(`.${klass.swiper}`, this._createSwiperOption());
  }

  private _createSwiperOption() {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    if (is_mobile) {
      return {
        modules: [Navigation, EffectFade],
        loop: true,
        spaceBetween: 24,
        navigation: {
          nextEl: 'button.right',
          prevEl: 'button.left',
        },
        on: {
          slideChange: (swiper: Swiper) => {
            this.setCurrentIndex(swiper.realIndex + 1);
            this.redraw();
          },
        },
      };
    } else {
      return {
        modules: [Navigation, EffectFade, Autoplay],
        loop: true,
        autoplay: {
          delay: 5000,
          disableOnInteraction: false,
        },
        spaceBetween: 24,
        allowTouchMove: true,
        simulateTouch: true,
        navigation: {
          nextEl: 'button.right',
          prevEl: 'button.left',
        },
        on: {
          slideChange: (swiper: Swiper) => {
            this.setCurrentIndex(swiper.realIndex + 1);
            this.redraw();
          },
        },
      };
    }
  }

  calcProgress() {
    return ((this.current_index + 1) / (this.max_index + 1)) * 100;
  }

  setCurrentIndex(index: number) {
    this.current_index = index;
  }

  override redraw(): this {
    $(this.element()).find('.current_index')?.setInnerHtml(`0${this.current_index.toString()}`);
    const progress = $(this.element()).find('.progress')?.element();
    if (progress) {
      progress.style.width = `${this.calcProgress()}%`;
    }

    return this;
  }

  private _renderCardList() {
    return pipe(
      range(1, 6),
      map((card_number) => new CreatorCard({ card_number })),
      map((CreatorCard) => html`<div class="swiper-slide">${CreatorCard}</div>`),
      toArray,
    );
  }
}
